import { SearchParamsOption } from "ky";
import { api, Api } from "@api/base-api-request.ts";
import { TokenTransactionResponse } from "@api/token-screener/types.ts";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { SelectMultipleButtonOption } from "@ui/select-multiple-buttons.tsx";
import { useCallback } from "react";

const QUERY_KEY = "screener-transactions";

export type ScreenerTransactionsParams = SearchParamsOption & {
  tokenId: string;
  cursor?: string | null;
  limit?: number;
  wallet?: string | null;
  filters?: {
    price?: {
      min?: number;
      max?: number;
    };
    outflow?: {
      min?: number;
      max?: number;
    };
    inflow?: {
      min?: number;
      max?: number;
    };
    value?: {
      min?: number;
      max?: number;
    };
    txType?: SelectMultipleButtonOption[];
    time?: {
      min?: number;
      max?: number;
    };
  };
};

const transformParams = (params: ScreenerTransactionsParams) => {
  const result: Record<string, string | number> = {};
  if (params.wallet != null) {
    result.wallet = params.wallet;
  }
  if (params.cursor != null) {
    result.cursor = params.cursor;
  }
  if (params.limit != null) {
    result.limit = params.limit;
  }
  if (params.filters != null && Object.keys(params.filters).length > 0) {
    result.filters = JSON.stringify(params.filters);
  }

  return result;
};

const fetchScreenerTransactions = async (
  api: Api,
  params: ScreenerTransactionsParams,
): Promise<TokenTransactionResponse> => {
  return api
    .get(`tokenScreener/${params.tokenId}/transactions`, {
      searchParams: transformParams(params),
    })
    .json();
};

const defaultSelector = (
  data: TokenTransactionResponse,
): TokenTransactionResponse => data;

const useGetScreenerTransactions = <TData = TokenTransactionResponse>(
  params: ScreenerTransactionsParams,
  selector: (data: TokenTransactionResponse) => TData = defaultSelector as (
    data: TokenTransactionResponse,
  ) => TData,
  options?: Partial<UseQueryOptions<TokenTransactionResponse, Error, TData>>,
) => {
  return useQuery<TokenTransactionResponse, Error, TData>({
    queryKey: [QUERY_KEY, params],
    queryFn: async () => fetchScreenerTransactions(api, params),
    placeholderData: (data) => data,
    select: useCallback(selector, [selector]),
    ...options,
  });
};

export {
  fetchScreenerTransactions,
  useGetScreenerTransactions,
  QUERY_KEY as SCREENER_TRANSACTIONS_QUERY,
};
